import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import {
  PrismBadge,
  PrismButton,
  PrismIcon,
  PrismIconBrand,
  PrismSelectInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type { IconBrandName } from '@mylighthouse/prism-ember/types/components/prism/icon-brand';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t } from 'ember-intl';
import { and, gt, not } from 'ember-truth-helpers';
import type { Ota } from 'frontend-utils/models';
import type { PrimaSettingsInProgressRatePreference } from 'frontend-utils/models/hotel';
import type { RoomType } from 'frontend-utils/models/roomtype';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import type { RoomCategoryFilter } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  MEALTYPE_ICONS,
  MEALTYPE_TRANSLATION_KEYS,
  type MealTypeValues,
  type RateParam,
  ROOMTYPE_TRANSLATION_KEYS,
} from 'lumen/utils/constants/general-constants';
import {
  OPEN_PRICING_ROOM_CATEGORY_SORT_ORDER,
  OPEN_ROOM_PRICING_ROOM_CATEGORIES,
  OPEN_ROOM_PRICING_ROOM_CATEGORIES_TRANSLATIONS,
  type OpenRoomPricingFixedCategory,
  RATE_TYPE_TO_TRANSLATION_KEYS,
  RATE_TYPES_ICONS,
  RI_ROOMTYPE_TO_OPEN_PRICING_ROOM_CATEGORY_MAPPING,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { preferredChannelInProgressName } from 'lumen/utils/pricing-assistant/preferred-channel-utils';
import {
  isMinDaysBetweenEditingElapsed,
  timeUntilNextAllowedUpdate,
} from 'lumen/utils/pricing-assistant/preferred-rate-editing-utils';

type OpenRoomPricingRoomTypeMappingOption = Option & {
  id: OpenRoomPricingFixedCategory;
};

interface HotelPricingAssistantFiltersPricingSelectionsSignature {
  Args: {
    isPeriodsRequestLoading: boolean;
    isTableView: boolean;
    los: number;
    maxPersons: string | null;
    mealPlan: MealTypeValues | null;
    rateParam: RateParam | null;
    roomType: string | null;
    selectedOTA: Ota | null;
    updateGuests: (maxPersons: string | null) => void;
    updateRoomType: (roomType: RoomType | null) => void;
  };
}

export default class HotelPricingAssistantFiltersPricingSelections extends Component<HotelPricingAssistantFiltersPricingSelectionsSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  @cached
  get availableFilterOptions(): RoomCategoryFilter[] {
    if (this.args.isPeriodsRequestLoading) {
      return [];
    }
    return this.store
      .peekAll<RoomCategoryFilter>('room-category-filter')
      .slice();
  }

  get displayEditbutton(): boolean {
    return (
      !this.access
        .isPricingAssistantPreferredRateSettingsActionOrInputDisabled &&
      this.access.hasPricingAssistantSettingsPreferredRateTab
    );
  }

  @cached
  get guestOptions(): { id: number; label: string }[] {
    if (this.args.isPeriodsRequestLoading) {
      const { persons } = this.hotelAndMarketService.hotel.paRatePreference;

      return [
        {
          id: persons,
          label: this.intl.t('rates.persons_selection', {
            count: persons,
          }),
        },
      ];
    }

    const availableGuestOptionsForSelectedRoomType =
      this.availableFilterOptions.find((filter) => {
        return filter.roomCategory === this.selectedRoomTypeMapping?.id;
      })?.occupancy;

    if (availableGuestOptionsForSelectedRoomType) {
      return availableGuestOptionsForSelectedRoomType
        .map((option) => {
          return {
            id: option,
            label: this.intl.t('rates.persons_selection', { count: option }),
          };
        })
        .sort((a, b) => a.id - b.id);
    }

    return [];
  }

  get icon(): IconBrandName {
    return this.args.selectedOTA?.id as IconBrandName;
  }

  get isEditButtonDisabled(): boolean {
    return !isMinDaysBetweenEditingElapsed(
      this.hotelAndMarketService.hotel.paRatePreference.last_edited,
    );
  }

  get preferredChannelInProgressName(): string {
    return preferredChannelInProgressName(
      this.hotelAndMarketService.hotel.recommendationsChannels,
      this.store,
    );
  }

  get ratePreferenceSettingsInProgress():
    | PrimaSettingsInProgressRatePreference
    | undefined {
    const { primaSettingsInProgress } = this.hotelAndMarketService.hotel;

    // primaSettingsInProgress if a config update has ever been requested, so added optional chaining here
    return primaSettingsInProgress?.rate_preference;
  }

  get roomTypeMapping(): Record<string, string> {
    return this.hotelAndMarketService.hotel.roomTypeMapping;
  }

  @cached
  get roomTypeMappingOptions(): OpenRoomPricingRoomTypeMappingOption[] {
    const leadingRoom =
      this.hotelAndMarketService.hotel.caxxxPmsSettings.pms_leading_room_id;
    // we can depend on this.roomTypeMapping[leadingRoom] to always map to a valid OpenRoomPricingFixedCategory
    const leadingRoomCategory = this.roomTypeMapping[
      leadingRoom
    ] as OpenRoomPricingFixedCategory;

    if (this.args.isPeriodsRequestLoading) {
      // return the paRatePreference while disabled
      const roomType =
        this.hotelAndMarketService.hotel.paRatePreference.room_type;
      const translationKey = `${ROOMTYPE_TRANSLATION_KEYS[roomType.id]}`;
      const label = this.intl.t(translationKey);

      return [{ id: leadingRoomCategory, label }];
    }

    return this.availableFilterOptions
      .map((filter) => filter.roomCategory)
      .sort(
        (a, b) =>
          OPEN_PRICING_ROOM_CATEGORY_SORT_ORDER.indexOf(a) -
          OPEN_PRICING_ROOM_CATEGORY_SORT_ORDER.indexOf(b),
      )
      .map((roomCategory) => {
        const translatedName = OPEN_ROOM_PRICING_ROOM_CATEGORIES_TRANSLATIONS[
          roomCategory
        ]
          ? this.intl.t(
              OPEN_ROOM_PRICING_ROOM_CATEGORIES_TRANSLATIONS[roomCategory],
            )
          : roomCategory;

        return {
          id: roomCategory,
          label: translatedName,
          value: translatedName,
          badge:
            leadingRoomCategory === roomCategory &&
            leadingRoomCategory !== OPEN_ROOM_PRICING_ROOM_CATEGORIES.UNMAPPED
              ? this.intl.t('pricing_assistant.room_type_mapping.badge.base')
              : '',
        };
      });
  }

  get selectedGuest(): { id: number; label: string } | undefined {
    if (!this.args.maxPersons) {
      return this.guestOptions?.[0];
    }

    const optionMatchingArgRequest = this.guestOptions?.find(
      (option) => option.id === parseInt(this.args.maxPersons!, 10),
    );

    return optionMatchingArgRequest || this.guestOptions?.[0];
  }

  get selectedMealtype(): {
    icon: IconName | undefined;
    id: MealTypeValues;
    title: string;
  } | null {
    if (!this.args.mealPlan) {
      return null;
    }

    return {
      id: this.args.mealPlan,
      icon: MEALTYPE_ICONS[this.args.mealPlan],
      title: this.intl.t(MEALTYPE_TRANSLATION_KEYS[this.args.mealPlan]),
    };
  }

  get selectedRateType(): {
    icon: IconName | undefined;
    id: RateParam;
    value: string;
  } | null {
    // The rate type shown in the filter bar is the Booking.com matching rate type selected in the PMS Settings
    const preferredRootRateType = this.args.rateParam;

    if (!preferredRootRateType) {
      return null;
    }

    // Extra check for the "value" to prevent a hard crash in case BE returns a rate type that is not supported by FE and thus doesn't have a mapped translation key
    return {
      value: RATE_TYPE_TO_TRANSLATION_KEYS[preferredRootRateType]
        ? this.intl.t(RATE_TYPE_TO_TRANSLATION_KEYS[preferredRootRateType])
        : preferredRootRateType,
      icon: RATE_TYPES_ICONS[preferredRootRateType],
      id: preferredRootRateType,
    };
  }

  get selectedRoomType(): string {
    const selectedRoomTypeObject =
      this.hotelAndMarketService.hotel.paRatePreference.room_type;

    return ROOMTYPE_TRANSLATION_KEYS[selectedRoomTypeObject.id]
      ? this.intl.t(ROOMTYPE_TRANSLATION_KEYS[selectedRoomTypeObject.id])
      : selectedRoomTypeObject.name; // We directly get the pretty names for custom room types
  }

  get selectedRoomTypeMapping():
    | OpenRoomPricingRoomTypeMappingOption
    | undefined {
    const { roomType } = this.args;

    if (!roomType) {
      return this.roomTypeMappingOptions[0];
    }

    const roomCategory =
      RI_ROOMTYPE_TO_OPEN_PRICING_ROOM_CATEGORY_MAPPING[roomType];

    const requestedOptionInUrl = this.roomTypeMappingOptions.find(
      (option) => option.id === roomCategory,
    );

    return requestedOptionInUrl || this.roomTypeMappingOptions[0];
  }

  get timeUntilNextAllowedUpdate(): string {
    return timeUntilNextAllowedUpdate(
      this.hotelAndMarketService.hotel.paRatePreference.last_edited,
    );
  }

  @action
  onGuestsChange(option: Option): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.args.updateGuests(option['id']);
  }

  @action
  onRoomTypeChange(option: OpenRoomPricingRoomTypeMappingOption): void {
    this.args.updateRoomType(option.id as RoomType);

    // On room type change, as default selected guest, we select 2 persons if available, otherwise the first available option
    const defaultSelectedGuestOnRoomTypeChange =
      this.guestOptions?.find((guestOption) => guestOption.id === 2) ||
      this.guestOptions?.[0];

    if (defaultSelectedGuestOnRoomTypeChange) {
      this.onGuestsChange(defaultSelectedGuestOnRoomTypeChange);
    }
  }

  @action
  redirectToPreferredRateSettings(): void {
    this.router.transitionTo('hotel.settings.preferred-rate');
  }

  <template>
    <div
      class="selections-container-with-hover flex flex-row items-center text-nowrap"
    >
      <span class="selections-label subdued-text mr-200">{{t
          "rates.pricing_page_mode.selections.label"
        }}</span>

      {{#if this.selectedRateType}}
        <div
          class="no-filter-content flex items-center gap-100"
          data-test-id="rate-type-filter"
        >
          {{#if this.selectedRateType.icon}}
            <PrismIcon @name={{this.selectedRateType.icon}} @size="small" />
          {{/if}}
          {{this.selectedRateType.value}}
        </div>
      {{/if}}

      <span class="subdued-text text-200--regular">-</span>

      <div
        class="no-filter-content flex items-center gap-100"
        data-test-id="ota-filter"
      >
        {{#if @selectedOTA.id}}
          <PrismIconBrand @name={{this.icon}} />
        {{/if}}
        {{@selectedOTA.name}}
        {{#if this.preferredChannelInProgressName}}
          <div>
            <PrismBadge
              @iconName="sync"
              @isSmall={{true}}
              @label=""
              @type="info"
            />
            <PrismTooltip
              @side="top"
              @text={{t
                "pricing_assistant.preferred_rate.channel_in_progress.tooltip"
                channel=this.preferredChannelInProgressName
              }}
            />
          </div>
        {{/if}}
      </div>

      <span class="subdued-text text-200--regular">-</span>

      <div
        class="no-filter-content flex items-center gap-100"
        data-test-id="los-filter"
      >
        <PrismIcon @name="meals-room-only" @size="small" />
        {{t "rates.nights" count=@los}}

        {{#if this.ratePreferenceSettingsInProgress.los}}
          <div>
            <PrismBadge
              @iconName="sync"
              @isSmall={{true}}
              @label=""
              @type="info"
            />
            <PrismTooltip
              @side="top"
              @text={{t
                "pricing_assistant.preferred_rate.los_persons_in_progress.tooltip"
              }}
            />
          </div>
        {{else if (gt @los 1)}}
          <GeneralIconWithTooltip
            @side="bottom"
            @tooltipContent={{t "pricing_assistant.selections.los.tooltip"}}
          />
        {{/if}}
      </div>

      <span class="subdued-text text-200--regular">-</span>

      <div
        class="no-filter-content flex items-center gap-100"
        data-test-id="guests-filter"
      >
        {{#if
          (and
            this.access.hasPricingAssistantOpenPricingFeature (not @isTableView)
          )
        }}
          <PrismSelectInput
            @disabled={{@isPeriodsRequestLoading}}
            @onChange={{this.onGuestsChange}}
            @options={{this.guestOptions}}
            @value={{this.selectedGuest}}
          />
        {{else}}
          <PrismIcon @name="user-group" @size="small" />
          {{#if @isTableView}}
            {{t "rates.persons_selection" count=@maxPersons}}
          {{else}}
            {{t "rates.persons_selection" count=@maxPersons}}
            {{#if this.ratePreferenceSettingsInProgress.persons}}
              <div>
                <PrismBadge
                  @iconName="sync"
                  @isSmall={{true}}
                  @label=""
                  @type="info"
                />
                <PrismTooltip
                  @side="top"
                  @text={{t
                    "pricing_assistant.preferred_rate.los_persons_in_progress.tooltip"
                  }}
                />
              </div>
            {{/if}}
          {{/if}}
        {{/if}}
      </div>

      <span class="subdued-text text-200--regular">-</span>

      {{#unless @isTableView}}
        <div class="no-filter-content flex items-center gap-100">
          {{#if this.access.hasPricingAssistantOpenPricingFeature}}
            <PrismSelectInput
              @disabled={{@isPeriodsRequestLoading}}
              @onChange={{this.onRoomTypeChange}}
              @options={{this.roomTypeMappingOptions}}
              @value={{this.selectedRoomTypeMapping}}
            />
          {{else}}
            {{this.selectedRoomType}}
          {{/if}}
        </div>

        <span class="subdued-text text-200--regular">-</span>
      {{/unless}}

      {{#if this.selectedMealtype}}
        <div
          class="no-filter-content flex items-center gap-100"
          data-test-id="mealtype-filter"
        >
          {{#if this.selectedMealtype.icon}}
            <PrismIcon @name={{this.selectedMealtype.icon}} @size="small" />
          {{/if}}
          {{this.selectedMealtype.title}}
        </div>
      {{/if}}

      {{! We don't show edit price for viewer users or integrated subs  }}
      {{#if this.displayEditbutton}}
        <div class="edit-button-wrapper flex items-center">
          <PrismButton
            @buttonType="secondary"
            @iconBefore="edit-pencil"
            @isDisabled={{this.isEditButtonDisabled}}
            @label={{t "general.edit"}}
            @onClick={{this.redirectToPreferredRateSettings}}
            @size="small"
          />
          {{#if this.isEditButtonDisabled}}
            <PrismTooltip
              @text={{t
                "pricing_assistant.preferred_rate.rate_filter.edit_button_tooltip"
                timeUntilEdit=this.timeUntilNextAllowedUpdate
              }}
            />
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
